import { Component, OnDestroy, OnInit } from '@angular/core';
import { StreamApi } from '@app/features/app-content/models/app-config.model';
import { SongHistoryConfig } from '@app/features/app-content/models/app-tabs.model';
import { UpdateAppConfig } from '@app/features/app-content/states/app-configuration.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { Store } from '@ngxs/store';
import { Subject, Subscription, debounceTime, filter } from 'rxjs';

@Component({
  selector: 'rk-song-history-configuration',
  templateUrl: './song-history-configuration.component.html',
  standalone: false,
})
export class SongHistoryConfigurationComponent implements OnInit, OnDestroy {
  currentAppConfigStreamList$ = this.store.select(
    AppConfigurationState.currentAppConfigStreamList,
  );

  private readonly subscriptions = new Subscription();
  private readonly streamConfigChanges = new Map<
    number,
    Subject<Partial<SongHistoryConfig>>
  >();

  pageIndex: number;
  streams: StreamApi[] = [];

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.currentAppConfigStreamList$.pipe(filter(streams => !!streams)).subscribe(streams => {
        this.streams = streams;
      }),
    );
  }

  onConfigToggleChanged(streamId: number, field: keyof SongHistoryConfig): void {
    const stream = this.streams.find(s => s.idflux === streamId);
    if (!stream) {
      return;
    }

    const streamIndex = this.streams.indexOf(stream);
    const currentValue = stream.ckoiConfiguration?.[field] ?? false;

    this.store.dispatch(
      new UpdateAppConfig(
        ['streams', streamIndex.toString(), 'ckoiConfiguration', field],
        !currentValue,
      ),
    );
  }

  onApiUrlChanged(event: Event, streamId: number): void {
    const target = event.target as HTMLInputElement;
    const stream = this.streams.find(s => s.idflux === streamId);
    if (!stream) {
      return;
    }

    const streamIndex = this.streams.indexOf(stream);

    if (!this.streamConfigChanges.has(streamId)) {
      const subject = new Subject<Partial<SongHistoryConfig>>();
      this.streamConfigChanges.set(streamId, subject);

      this.subscriptions.add(
        subject.pipe(debounceTime(500)).subscribe(changes => {
          Object.entries(changes).forEach(([key, value]) => {
            this.store.dispatch(
              new UpdateAppConfig(
                ['streams', streamIndex.toString(), 'ckoiConfiguration', key],
                value,
              ),
            );
          });
        }),
      );
    }

    this.streamConfigChanges.get(streamId).next({ apiUrl: target.value });
  }

  onSearchApiUrlChanged(event: Event, streamId: number): void {
    const target = event.target as HTMLInputElement;
    const stream = this.streams.find(s => s.idflux === streamId);
    if (!stream) {
      return;
    }

    const streamIndex = this.streams.indexOf(stream);

    if (!this.streamConfigChanges.has(streamId)) {
      const subject = new Subject<Partial<SongHistoryConfig>>();
      this.streamConfigChanges.set(streamId, subject);

      this.subscriptions.add(
        subject.pipe(debounceTime(500)).subscribe(changes => {
          Object.entries(changes).forEach(([key, value]) => {
            this.store.dispatch(
              new UpdateAppConfig(
                ['streams', streamIndex.toString(), 'ckoiConfiguration', key],
                value,
              ),
            );
          });
        }),
      );
    }

    this.streamConfigChanges.get(streamId).next({ searchApiUrl: target.value });
  }

  isValidUrl(url: string): boolean {
    if (!url || url === '') {
      return true;
    }

    try {
      const urlObj = new URL(url);

      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.streamConfigChanges.forEach(subject => subject.complete());
  }
}
